
import { useState, useEffect, useCallback } from 'react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

interface Lead {
  _id: string
  nome: string
  contato: string
  status: 'prospect' | 'negociacao' | 'perdido' | 'fechado'
  origem: string
  valor: number
  responsavel: string
  observacoes?: string
  createdAt: string
  updatedAt: string
}

interface LeadMetrics {
  totalLeads: number
  leadsFechados: number
  valorTotalFechado: number
  valorEmNegociacao: number
}

export const useLeads = () => {
  const [leads, setLeads] = useState<Lead[]>([])
  const [metrics, setMetrics] = useState<LeadMetrics>({
    totalLeads: 0,
    leadsFechados: 0,
    valorTotalFechado: 0,
    valorEmNegociacao: 0
  })
  const [loading, setLoading] = useState(false)

  const fetchLeads = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.leads.list()
      setLeads(list)
      calculateMetrics(list)
    } catch (error: any) {
      console.error('Erro ao buscar leads:', error)
      toast.error('Erro ao carregar leads')
    } finally {
      setLoading(false)
    }
  }, [])

  const calculateMetrics = (leadsList: Lead[]) => {
    const totalLeads = leadsList.length
    const leadsFechados = leadsList.filter(lead => lead.status === 'fechado').length
    const valorTotalFechado = leadsList
      .filter(lead => lead.status === 'fechado')
      .reduce((sum, lead) => sum + lead.valor, 0)
    const valorEmNegociacao = leadsList
      .filter(lead => lead.status === 'negociacao')
      .reduce((sum, lead) => sum + lead.valor, 0)

    setMetrics({
      totalLeads,
      leadsFechados,
      valorTotalFechado,
      valorEmNegociacao
    })
  }

  const createLead = async (leadData: Omit<Lead, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = new Date().toISOString()
      const newLead = await lumi.entities.leads.create({
        ...leadData,
        valor: Number(leadData.valor),
        createdAt: now,
        updatedAt: now
      })
      await fetchLeads()
      toast.success('Lead cadastrado com sucesso!')
      return newLead
    } catch (error: any) {
      console.error('Erro ao criar lead:', error)
      toast.error('Erro ao cadastrar lead')
      throw error
    }
  }

  const updateLead = async (leadId: string, updates: Partial<Lead>) => {
    if (typeof leadId !== 'string') {
      throw new Error('ID do lead deve ser uma string')
    }

    try {
      const processedUpdates = {
        ...updates,
        updatedAt: new Date().toISOString()
      }

      if (updates.valor !== undefined) {
        processedUpdates.valor = Number(updates.valor)
      }

      const updatedLead = await lumi.entities.leads.update(leadId, processedUpdates)
      await fetchLeads()
      toast.success('Lead atualizado com sucesso!')
      return updatedLead
    } catch (error: any) {
      console.error('Erro ao atualizar lead:', error)
      toast.error('Erro ao atualizar lead')
      throw error
    }
  }

  const deleteLead = async (leadId: string) => {
    if (typeof leadId !== 'string' || leadId === '[object Object]') {
      throw new Error('ID do lead inválido')
    }

    try {
      await lumi.entities.leads.delete(leadId)
      await fetchLeads()
      toast.success('Lead excluído com sucesso!')
    } catch (error: any) {
      console.error('Erro ao excluir lead:', error)
      toast.error('Erro ao excluir lead')
      throw error
    }
  }

  useEffect(() => {
    fetchLeads()
  }, [fetchLeads])

  return {
    leads,
    metrics,
    loading,
    fetchLeads,
    createLead,
    updateLead,
    deleteLead
  }
}
